/**
 * Courbe d'un chiffre clé (admin, statistiques en direct) : la période choisie en trait plein, la période
 * d'avant en pointillés. Survol à la souris, toucher ou flèches du clavier → détail de la tranche.
 */
import { useState, type KeyboardEvent, type PointerEvent } from 'react';
import { linePath, niceMax } from '@/lib/admin/live-stats';

const W = 300;
const H = 110;

export interface StatsChartProps {
  label: string;
  cur: number[];
  prev: number[];
  /** libellé court sous l'axe (« 14 h ») */
  ticks: string[];
  /** libellé complet de chaque tranche (« mardi 28 octobre ») */
  titles: string[];
  format: (v: number) => string;
  prevLabel: string;
}

export function StatsChart({
  label,
  cur,
  prev,
  ticks,
  titles,
  format,
  prevLabel,
}: StatsChartProps) {
  const [sel, setSel] = useState<number | null>(null);
  const n = cur.length;
  const max = niceMax(Math.max(0, ...cur, ...prev));
  const curPath = linePath(cur, max, W, H);
  const prevPath = linePath(prev, max, W, H);
  const area = curPath ? `${curPath} L${W},${H} L0,${H} Z` : '';

  const pick = (e: PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width || n < 1) return;
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setSel(Math.round(ratio * (n - 1)));
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const d = e.key === 'ArrowRight' ? 1 : -1;
      setSel((s) => Math.min(n - 1, Math.max(0, (s ?? (d > 0 ? -1 : n)) + d)));
    } else if (e.key === 'Escape') setSel(null);
  };

  const x = sel != null && n > 1 ? (sel / (n - 1)) * 100 : 0;
  const yOf = (v: number) => 100 - (Math.max(0, v) / max) * 100;
  const mid = Math.floor((n - 1) / 2);

  return (
    <div>
      <p className="text-ink-600 mb-1 min-h-[2.5rem] text-xs" aria-live="polite">
        {sel != null ? (
          <>
            <span className="text-ink-900 font-semibold">{titles[sel]}</span>
            {' · '}
            <span className="text-ink-900 font-semibold">{format(cur[sel] ?? 0)}</span>
            <span className="text-ink-500"> (avant : {format(prev[sel] ?? 0)})</span>
          </>
        ) : (
          <span className="text-ink-400">Touche la courbe pour voir le détail.</span>
        )}
      </p>
      <div className="flex gap-1">
        <div className="text-ink-400 flex w-12 shrink-0 flex-col justify-between text-right text-[11px] leading-none">
          <span>{format(max)}</span>
          <span>{format(0)}</span>
        </div>
        <div
          role="group"
          tabIndex={0}
          aria-label={`${label} : courbe. Flèches gauche et droite pour parcourir.`}
          className="focus-visible:ring-primary-500 relative h-[110px] flex-1 cursor-crosshair touch-pan-y rounded-md outline-none focus-visible:ring-2"
          onPointerMove={pick}
          onPointerDown={pick}
          onPointerLeave={(e) => {
            if (e.pointerType === 'mouse') setSel(null);
          }}
          onKeyDown={onKey}
          onBlur={() => setSel(null)}
        >
          <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full overflow-visible"
            aria-hidden
          >
            {[0, 0.5, 1].map((r) => (
              <line
                key={r}
                x1={0}
                x2={W}
                y1={H * r}
                y2={H * r}
                className="stroke-ink-100"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {area && <path d={area} className="fill-primary-500/10" />}
            {prevPath && (
              <path
                d={prevPath}
                fill="none"
                className="stroke-ink-300"
                strokeWidth={1.5}
                strokeDasharray="4 3"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            )}
            {curPath && (
              <path
                d={curPath}
                fill="none"
                className="stroke-primary-500"
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
          {sel != null && (
            <>
              <span
                className="bg-ink-300 pointer-events-none absolute inset-y-0 w-px"
                style={{ left: `${x}%` }}
                aria-hidden
              />
              <span
                className="border-ink-400 pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-white"
                style={{ left: `${x}%`, top: `${yOf(prev[sel] ?? 0)}%` }}
                aria-hidden
              />
              <span
                className="bg-primary-500 pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-white"
                style={{ left: `${x}%`, top: `${yOf(cur[sel] ?? 0)}%` }}
                aria-hidden
              />
            </>
          )}
        </div>
      </div>
      {n > 0 && (
        <div className="text-ink-400 ml-[3.25rem] mt-1 flex justify-between text-[11px]">
          <span>{ticks[0]}</span>
          {n > 2 && <span>{ticks[mid]}</span>}
          <span>{ticks[n - 1]}</span>
        </div>
      )}
      <details className="mt-1 text-xs">
        <summary className="text-ink-500 min-h-[2.75rem] cursor-pointer py-3">
          Voir les chiffres
        </summary>
        <div className="max-h-60 overflow-y-auto">
          <table className="w-full text-left">
            <thead className="text-ink-500">
              <tr>
                <th className="py-1 font-semibold">Tranche</th>
                <th className="py-1 text-right font-semibold">{label}</th>
                <th className="py-1 text-right font-semibold">{prevLabel}</th>
              </tr>
            </thead>
            <tbody className="text-ink-800">
              {cur.map((v, i) => (
                <tr key={i} className="border-ink-100 border-t">
                  <td className="py-1">{titles[i]}</td>
                  <td className="py-1 text-right tabular-nums">{format(v)}</td>
                  <td className="text-ink-500 py-1 text-right tabular-nums">
                    {format(prev[i] ?? 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
